import cx from 'classnames';
import { forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { getIsTyping } from 'store/ticket-service/selector';
import { getUserById } from 'store/user-service/asyncActions';
import { getLoadedUsers } from 'store/user-service/selector';

import { MessageSender, NamespacesList } from 'core/enums/common';

import { Message } from 'components/Chatbot/Message/Message';
import { SystemMessage } from 'components/Chatbot/Message/SystemMessage';
import { IMessage } from 'components/Chatbot/Message/types';
import Loader from 'components/Loader/Loader';

import styles from './MessageList.module.scss';

interface IMessagesListProps {
	list: Array<IMessage>;
	isChatLoading: boolean;
}

export const MessageList = forwardRef<HTMLDivElement, IMessagesListProps>(
	({ list, isChatLoading }, ref) => {
		const { t: commonTranslate } = useTranslation(NamespacesList['common']);

		const dispatch = useAppDispatch();

		const isTyping = useSelector(getIsTyping);
		const loadedUsers = useSelector(getLoadedUsers);

		const getIsSystemMessage = (message: IMessage): boolean => {
			return message.sender === MessageSender.System;
		};

		useEffect(() => {
			const uniqueUserIds = [
				...new Set(list.map((message) => message.userId).filter((item) => item)),
			];
			const loadedIds = loadedUsers.map((user) => user.id);

			if (uniqueUserIds.length)
				uniqueUserIds.forEach((userId) => {
					if (!loadedIds.includes(userId)) dispatch(getUserById({ userId }));
				});
		}, [list, loadedUsers]);

		return (
			<div
				className={cx(styles.messages_list, { [styles['full-height']]: isChatLoading })}
				ref={ref}
			>
				<>
					{/* NOTE: to prevent loader display when back to existing tab */}
					{isChatLoading && !list.length ? (
						<Loader size={32} />
					) : (
						list.map((message, i) =>
							getIsSystemMessage(message) ? (
								<SystemMessage model={message} key={i} />
							) : (
								<Message model={message} key={i} />
							)
						)
					)}
					{isTyping && (
						<div className={styles['chat-typing']}>
							{isTyping && commonTranslate('loaders.chatIsTyping')}
						</div>
					)}
				</>
			</div>
		);
	}
);
